import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Calculator, FileText, Shield, ChevronRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import Logo from '../components/common/Logo';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <Logo size={40} />
              <span className="text-2xl font-bold text-white">FINBRO</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:bg-white/10">
                  Conectare
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-500 hover:bg-blue-600">
                  Înregistrare Intermediar
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Platforma Completă pentru
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"> Intermediari de Credite</span>
            </h1>
            <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
              Gestionează clienții, calculează oferte și generează documente profesionale. 
              Tot ce ai nevoie pentru activitatea ta de intermediere.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-lg px-8">
                  Începe Gratuit <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="text-white border-white/30 hover:bg-white/10 text-lg px-8">
                  Am deja cont
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Ce oferă platforma FINBRO?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: FileText,
                title: 'Formulare Online',
                description: 'Trimite linkuri clienților pentru completare documente'
              },
              {
                icon: Calculator,
                title: 'Calculator Credit',
                description: 'Compară ofertele de la toate băncile într-un singur loc'
              },
              {
                icon: Users,
                title: 'Gestionare Clienți',
                description: 'Urmărește statusul fiecărei cereri de credit'
              },
              {
                icon: Shield,
                title: 'Conform GDPR',
                description: 'Documente conforme cu OUG 52 și GDPR'
              }
            ].map((feature, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-6 hover:bg-white/15 transition-colors">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing/Plans Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Abonamente</h2>
          <p className="text-gray-400 text-center mb-12">Alege planul potrivit pentru tine</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* BASIC */}
            <div className="rounded-xl p-8 bg-white/10 flex flex-col">
              <h3 className="text-2xl font-bold text-white">BASIC</h3>
              <p className="text-3xl font-bold text-white mt-2">Gratuit</p>
              <ul className="mt-6 space-y-3 flex-grow">
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Formular client OUG 52 cu link</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Trimitere cereri pe email</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Vizualizare oferte bancare</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Calculator rate + scadențar</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Generare ofertă PDF</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Branding logo pe documente</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Istoric cereri</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Conform OUG 52 & OUG 50</span>
                </li>
              </ul>
              <Link to="/register" className="mt-8">
                <Button className="w-full bg-blue-500 hover:bg-blue-600">
                  Începe Gratuit
                </Button>
              </Link>
            </div>

            {/* PROFESIONAL */}
            <div className="rounded-xl p-8 bg-gradient-to-b from-blue-500 to-blue-600 ring-2 ring-blue-400 flex flex-col">
              <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full w-fit">
                POPULAR
              </span>
              <h3 className="text-2xl font-bold text-white mt-4">PROFESIONAL</h3>
              <p className="text-3xl font-bold text-white mt-2">99 RON/lună</p>
              <ul className="mt-6 space-y-3 flex-grow">
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="font-semibold">Tot din BASIC +</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Calculator taxe notariale</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Calculator salariu</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Calculator grad de îndatorare</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Fișe produse bancare actualizate la zi</span>
                </li>
              </ul>
              <Link to="/register" className="mt-8">
                <Button className="w-full bg-white text-blue-600 hover:bg-gray-100">
                  Alege PROFESIONAL
                </Button>
              </Link>
            </div>

            {/* PREMIUM */}
            <div className="rounded-xl p-8 bg-white/10 flex flex-col">
              <h3 className="text-2xl font-bold text-white">PREMIUM</h3>
              <p className="text-3xl font-bold text-white mt-2">199 RON/lună</p>
              <ul className="mt-6 space-y-3 flex-grow">
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="font-semibold">Tot din PROFESIONAL +</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Agent AI bancar</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Acces funcționalități noi</span>
                </li>
              </ul>
              <Link to="/register" className="mt-8">
                <Button className="w-full bg-blue-500 hover:bg-blue-600">
                  Alege PREMIUM
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Gata să îți crești afacerea?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Alătură-te comunității FINBRO - tot ce ai nevoie ca intermediar, într-un singur loc
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8">
              Creează cont gratuit
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <Logo size={32} />
              <span className="text-xl font-bold text-white">FINBRO</span>
            </div>
            <div className="flex gap-6 text-gray-400">
              <Link to="/terms" className="hover:text-white">Termeni și Condiții</Link>
              <Link to="/privacy" className="hover:text-white">Politica de Confidențialitate</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
              <Link to="/login" className="hover:text-white">Admin</Link>
            </div>
            <p className="text-gray-500">© 2025 FINBRO. Toate drepturile rezervate.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
